import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Affix,
  Button,
  DatePicker,
  Divider,
  Input,
  Radio,
  Spin,
  Upload,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router";
import moment from "moment";
import { Report, Loading } from "notiflix";

import Navbar from "../../../atoms/navbar";
import Footer from "../../../atoms/footer";
import ImportantInfo from "../../../atoms/important-updates";

import "./styles.css";

import { requestToken } from "../../../auth/AuthServices";
import HeaderFarms from "../../../utils/header_farms";
import API from "../../../services";
import { normalizeLevel } from "../utils";
import { NON_STAFF_NAMES } from "../../../constants/pa.constant";

const { TextArea } = Input;

const currentYear = 2025;
const DRAFT_STORAGE_KEY = "pa-create-draft";
const MAX_EVIDENCE_SIZE = 10 * 1024 * 1024; // 10 MB
const I_LOVE_PDF_URL = "https://www.ilovepdf.com";

const formatFileSize = (size) => {
  if (!size) return "";
  if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(size / 1024).toFixed(1)} KB`;
};

const buildTask = (year) => ({
  description: "",
  kpi_indicator: "",
  achievement_date: null,
  result_comment: "",
  note: "",
  year,
  evidence_file_name: "",
  evidence_file_url: "",
  evidence_file_key: "",
  evidence_file_size: null,
  evidence_uploaded_at: null,
});

const normalizeTasks = (savedList, defaultYear) => {
  const list = Array.isArray(savedList)
    ? savedList.map((task) => ({
        ...buildTask(defaultYear),
        ...task,
        year: defaultYear,
        achievement_date: task?.achievement_date
          ? moment(task.achievement_date)
          : null,
      }))
    : [];

  while (list.length < 3) {
    list.push(buildTask(defaultYear));
  }

  return list;
};

const hydratePerformanceValues = (saved = {}, master = []) => {
  const map = {};
  master.forEach((aspect) => {
    const raw = saved?.[aspect.aspect_code];
    let normalized = null;
    if (typeof raw === "number") {
      normalized = raw;
    } else if (Array.isArray(raw)) {
      const firstNumber = raw.find((val) => typeof val === "number");
      normalized = typeof firstNumber === "number" ? firstNumber : null;
    }
    map[aspect.aspect_code] = normalized;
  });
  return map;
};

const hydrateCompetencyValues = (saved = {}, master = []) => {
  const map = {};
  master.forEach((comp) => {
    comp.statements.forEach((stmt) => {
      const key = `${comp.competency_code}_${stmt.statement_no}`;
      const savedScore = saved?.[key];
      map[key] = [1, 2, 3].includes(savedScore) ? savedScore : null;
    });
  });
  return map;
};

const serializeTasks = (tasks = []) =>
  tasks.map((task) => ({
    ...task,
    achievement_date: task.achievement_date
      ? moment(task.achievement_date).toISOString()
      : null,
  }));

function PATransactionCreate() {
  const navigate = useNavigate();
  const [session] = useState(
    JSON.parse(localStorage.getItem("session_farms") || "{}")
  );
  const [loadingMaster, setLoadingMaster] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [performanceMaster, setPerformanceMaster] = useState([]);
  const [competencyMaster, setCompetencyMaster] = useState([]);
  const [performanceValues, setPerformanceValues] = useState({});
  const [competencyValues, setCompetencyValues] = useState({});
  const [pastTasks, setPastTasks] = useState(
    Array.from({ length: 3 }, () => buildTask(currentYear))
  );
  const [futureTasks, setFutureTasks] = useState(
    Array.from({ length: 3 }, () => buildTask(currentYear + 1))
  );
  const [draftInitialized, setDraftInitialized] = useState(false);
  const [evidenceLoading, setEvidenceLoading] = useState({});

  const levelForMaster = useMemo(
    () => normalizeLevel(session?.job_level || ""),
    [session?.job_level]
  );

  const isNonStaff = NON_STAFF_NAMES.includes(levelForMaster);

  const loadMasterData = async () => {
    if (!session?.employee_id) {
      Report.warning(
        "Data tidak ditemukan",
        "Session karyawan tidak tersedia. Silakan login ulang.",
        "Mengerti"
      );
      return navigate("/home");
    }

    try {
      setLoadingMaster(true);
      const accessToken = await requestToken();
      const params = { year: currentYear, level: levelForMaster };

      const [performanceResp, competencyResp] = await Promise.all([
        API.ListPAMasterPerformance(HeaderFarms(accessToken), params),
        API.ListPAMasterCompetencies(HeaderFarms(accessToken), params),
      ]);

      const performanceData = performanceResp.data?.[0]?.aspects || [];
      const competencyData = competencyResp.data?.[0]?.competencies || [];

      setPerformanceMaster(performanceData);
      setCompetencyMaster(competencyData);

      const perfInitial = {};
      performanceData.forEach((aspect) => {
        perfInitial[aspect.aspect_code] = null;
      });
      setPerformanceValues(perfInitial);

      const compInitial = {};
      competencyData.forEach((comp) => {
        comp.statements.forEach((stmt) => {
          const key = `${comp.competency_code}_${stmt.statement_no}`;
          compInitial[key] = null;
        });
      });
      setCompetencyValues(compInitial);
    } catch (error) {
      console.error("loadMasterData error:", error);
      Report.warning(
        "Oops.. ada kendala",
        "Gagal memuat master Performance/Competency",
        "Mengerti"
      );
    } finally {
      setLoadingMaster(false);
    }
  };

  useEffect(() => {
    loadMasterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loadingMaster || draftInitialized) return;

    const savedRaw = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (savedRaw) {
      try {
        const saved = JSON.parse(savedRaw);

        if (
          saved?.employee_id === session?.employee_id &&
          saved?.year === currentYear
        ) {
          setPerformanceValues((prev) =>
            hydratePerformanceValues(saved.performanceValues, performanceMaster)
          );
          setCompetencyValues((prev) =>
            hydrateCompetencyValues(saved.competencyValues, competencyMaster)
          );
          setPastTasks(normalizeTasks(saved.pastTasks, currentYear));
          setFutureTasks(normalizeTasks(saved.futureTasks, currentYear + 1));
        } else {
          localStorage.removeItem(DRAFT_STORAGE_KEY);
        }
      } catch (error) {
        console.error("loadDraft error:", error);
        localStorage.removeItem(DRAFT_STORAGE_KEY);
      }
    }

    setDraftInitialized(true);
  }, [
    loadingMaster,
    draftInitialized,
    session?.employee_id,
    performanceMaster,
    competencyMaster,
  ]);

  useEffect(() => {
    if (
      loadingMaster ||
      !draftInitialized ||
      !session?.employee_id
    )
      return;

    const payload = {
      employee_id: session.employee_id,
      year: currentYear,
      saved_at: new Date().toISOString(),
      performanceValues,
      competencyValues,
      pastTasks: serializeTasks(pastTasks),
      futureTasks: serializeTasks(futureTasks),
    };

    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      console.error("saveDraft error:", error);
    }
  }, [
    performanceValues,
    competencyValues,
    pastTasks,
    futureTasks,
    loadingMaster,
    draftInitialized,
    session?.employee_id,
  ]);

  const handlePerformanceChange = (aspectCode, value) => {
    setPerformanceValues((prev) => ({
      ...prev,
      [aspectCode]: value,
    }));
  };

  const handleCompetencyChange = (key, value) => {
    setCompetencyValues((prev) => ({ ...prev, [key]: value }));
  };

  const updateTaskValue = (type, index, field, value) => {
    const stateSetter = type === "past" ? setPastTasks : setFutureTasks;
    const state = type === "past" ? pastTasks : futureTasks;

    const next = [...state];
    if (field && typeof field === "object") {
      next[index] = { ...next[index], ...field };
    } else {
      next[index] = { ...next[index], [field]: value };
    }
    stateSetter(next);
  };

  const addTaskRow = (type) => {
    const setter = type === "past" ? setPastTasks : setFutureTasks;
    const year = type === "past" ? currentYear : currentYear + 1;
    setter((prev) => [...prev, buildTask(year)]);
  };

  const removeTaskRow = (type, index) => {
    const setter = type === "past" ? setPastTasks : setFutureTasks;
    const list = type === "past" ? pastTasks : futureTasks;
    if (list.length <= 3) return;
    setter(list.filter((_, idx) => idx !== index));
  };

  const buildEvidenceKey = (type, index) => `${type}-${index}`;

  const handleEvidenceUpload = async (type, index, file) => {
    if (!file) return;

    if (file.type !== "application/pdf") {
      Report.warning(
        "Format tidak didukung",
        "Unggah file berformat PDF.",
        "Mengerti"
      );
      return;
    }

    if (file.size > MAX_EVIDENCE_SIZE) {
      Report.warning(
        "File terlalu besar",
        "Ukuran evidence maksimal 10MB.",
        "Mengerti"
      );
      return;
    }

    try {
      const key = buildEvidenceKey(type, index);
      setEvidenceLoading((prev) => ({ ...prev, [key]: true }));
      const token = await requestToken();
      const headers = {
        ...HeaderFarms(token),
      };
      const formData = new FormData();
      formData.append("file", file);
      formData.append("year", type === "past" ? currentYear : currentYear + 1);

      const response = await API.UploadPATaskEvidence(headers, formData);
      const meta = response.data || {};
      updateTaskValue(type, index, {
        evidence_file_name: meta.file_name || file.name,
        evidence_file_url: meta.file_url || "",
        evidence_file_key: meta.file_key || "",
        evidence_file_size: meta.file_size || file.size,
        evidence_uploaded_at: meta.uploaded_at || new Date().toISOString(),
      });
    } catch (error) {
      console.error("UploadPATaskEvidence error:", error);
      Report.warning(
        "Upload gagal",
        error?.response?.data?.message ||
          "Tidak dapat mengunggah evidence. Coba lagi.",
        "Mengerti"
      );
    } finally {
      setEvidenceLoading((prev) => {
        const key = buildEvidenceKey(type, index);
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleEvidenceRemove = (type, index) => {
    updateTaskValue(type, index, {
      evidence_file_name: "",
      evidence_file_url: "",
      evidence_file_key: "",
      evidence_file_size: null,
      evidence_uploaded_at: null,
    });
  };

  const validateForm = () => {
    const missingPerformance = performanceMaster.some((aspect) => {
      const value = performanceValues[aspect.aspect_code];
      return typeof value !== "number";
    });

    if (missingPerformance && !isNonStaff) {
      Report.warning(
        "Validasi Data",
        "Mohon pilih nilai untuk seluruh aspek kinerja.",
        "Mengerti"
      );
      return false;
    }

    const missingCompetency = competencyMaster.some((comp) =>
      comp.statements.some((stmt) => {
        const key = `${comp.competency_code}_${stmt.statement_no}`;
        return !competencyValues[key];
      })
    );

    if (missingCompetency) {
      Report.warning(
        "Validasi Data",
        "Mohon isi seluruh penilaian kompetensi.",
        "Mengerti"
      );
      return false;
    }

    if (pastTasks.length < 3 || futureTasks.length < 3) {
      Report.warning(
        "Validasi Data",
        "Minimal 3 tugas untuk periode berjalan dan perencanaan.",
        "Mengerti"
      );
      return false;
    }

    const invalidPast = pastTasks.some(
      (task) =>
        !task.description ||
        !task.kpi_indicator ||
        !task.achievement_date
    );
    if (!isNonStaff && invalidPast) {
      Report.warning(
        "Validasi Data",
        "Lengkapi seluruh kolom pada tugas 12 bulan terakhir.",
        "Mengerti"
      );
      return false;
    }

    const invalidFuture = futureTasks.some(
      (task) => !task.description || !task.kpi_indicator
    );
    if (!isNonStaff &&invalidFuture) {
      Report.warning(
        "Validasi Data",
        "Lengkapi seluruh kolom pada tugas 12 bulan berikutnya.",
        "Mengerti"
      );
      return false;
    }

    return true;
  };

  const buildPerformancePayload = () => {
    const payload = [];

    performanceMaster.forEach((aspect) => {
      const selectedValue = performanceValues[aspect.aspect_code];
      if (typeof selectedValue !== "number") return;
      payload.push({
        aspect_code: aspect.aspect_code,
        aspect_label: aspect.aspect_label,
        order: aspect.order,
        selected_value: selectedValue,
      });
    });

    return payload;
  };

  const buildCompetencyPayload = () => {
    const payload = [];
    competencyMaster.forEach((comp) => {
      comp.statements.forEach((stmt) => {
        const key = `${comp.competency_code}_${stmt.statement_no}`;
        payload.push({
          competency_code: comp.competency_code,
          statement_no: stmt.statement_no,
          level: levelForMaster,
          score: competencyValues[key],
        });
      });
    });
    return payload;
  };

  const mapTaskPayload = (task, orderNo) => ({
    order_no: orderNo,
    year: task.year,
    description: task.description,
    kpi_indicator: task.kpi_indicator,
    achievement_date: task.achievement_date
      ? moment(task.achievement_date).toISOString()
      : null,
    result_comment: task.result_comment,
    note: task.note,
    evidence_file_name: task.evidence_file_name || "",
    evidence_file_url: task.evidence_file_url || "",
    evidence_file_key: task.evidence_file_key || "",
    evidence_file_size: task.evidence_file_size || null,
    evidence_uploaded_at: task.evidence_uploaded_at || null,
  });

  const buildTaskPayload = () => {
    const past = pastTasks.map((task, idx) => mapTaskPayload(task, idx + 1));
    const future = futureTasks.map((task, idx) =>
      mapTaskPayload(task, pastTasks.length + idx + 1)
    );

    return [...past, ...future];
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      setSubmitting(true);
      Loading.dots("Mengirim penilaian...");
      const accessToken = await requestToken();

      const payload = {
        fullname: session.fullname || "",
        position: session.job_position || "",
        level: session.job_level || "",
        department: session.organization_name || "",
        period_year: currentYear,
        period_start: moment().startOf("year").toISOString(),
        period_end: moment().endOf("year").toISOString(),
        performances: buildPerformancePayload(),
        competencies: buildCompetencyPayload(),
        tasks: buildTaskPayload(),
      };

      await API.CreatePATransaction(HeaderFarms(accessToken), payload);
      
      // localStorage.removeItem(DRAFT_STORAGE_KEY);

      Report.success(
        "Berhasil dikirim",
        "Performance Appraisal berhasil diajukan.",
        "Lihat Riwayat",
        () => navigate("/pa/history")
      );
    } catch (error) {
      console.error("CreatePATransaction error:", error);
      Report.warning(
        "Oops.. ada kendala",
        error?.response?.data?.error?.[0]?.name ||
          "Gagal mengirim data Performance Appraisal.",
        "Mengerti"
      );
    } finally {
      setSubmitting(false);
      Loading.remove();
    }
  };

  const renderTaskSection = (title, tasks, type) => (
    <div className="pa-card">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <p className="pa-section-title mb-1">{title}</p>
          <small className="text-muted">
            Minimal 3 tugas. Isi seluruh kolom yang tersedia.
          </small>
        </div>
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => addTaskRow(type)}
        >
          Tambah Tugas
        </Button>
      </div>
      {tasks.map((task, index) => (
        <div key={`${type}-${index}`} className="pa-task-card">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <p className="mb-0 fw-bold">Tugas #{index + 1}</p>
            {tasks.length > 3 && (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeTaskRow(type, index)}
              >
                Hapus
              </Button>
            )}
          </div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="pa-field-label">Deskripsi Tugas <span className="text-danger">*</span></label>
              <TextArea
                rows={3}
                value={task.description}
                onChange={(e) =>
                  updateTaskValue(type, index, "description", e.target.value)
                }
              />
            </div>
            <div className="col-md-6">
              <label className="pa-field-label">Indikator / KPI <span className="text-danger">*</span></label>
              <TextArea
                rows={3}
                value={task.kpi_indicator}
                onChange={(e) =>
                  updateTaskValue(type, index, "kpi_indicator", e.target.value)
                }
              />
            </div>
            <div className="col-md-4">
              <label className="pa-field-label">Tanggal Pencapaian <span className="text-danger">*</span></label>
              <DatePicker
                style={{ width: "100%" }}
                format="DD MMM YYYY"
                value={task.achievement_date ? moment(task.achievement_date) : null}
                onChange={(date) =>
                  updateTaskValue(type, index, "achievement_date", date)
                }
              />
            </div>
            <div className="col-md-4">
              <label className="pa-field-label">
                {type === "past" ? "Hasil / Komentar" : "Catatan"}
              </label>
              <TextArea
                rows={2}
                value={type === "past" ? task.result_comment : task.note}
                onChange={(e) =>
                  updateTaskValue(
                    type,
                    index,
                    type === "past" ? "result_comment" : "note",
                    e.target.value
                  )
                }
              />
            </div>
            <div className="col-md-4">
              <label className="pa-field-label">Tahun <span className="text-danger">*</span></label>
              <Input value={task.year} disabled />
            </div>
            {type === "past" && (
              <div className="col-12">
                <label className="pa-field-label">
                  Evidence (PDF, maks 10MB)
                </label>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <a
                    href={I_LOVE_PDF_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="pa-evidence-helper-link ms-auto"
                  >
                    Butuh mengubah file ke PDF?
                  </a>
                </div>
                <div className="pa-task-evidence">
                  <Upload
                    showUploadList={false}
                    accept=".pdf"
                    beforeUpload={(file) => {
                      handleEvidenceUpload(type, index, file);
                      return false;
                    }}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={Boolean(
                        evidenceLoading[buildEvidenceKey(type, index)]
                      )}
                    >
                      Unggah Evidence
                    </Button>
                  </Upload>
                  {task.evidence_file_url ? (
                    <div className="pa-evidence-preview">
                      <a
                        href={task.evidence_file_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {task.evidence_file_name || "Lihat Evidence"}
                      </a>
                      {task.evidence_file_size ? (
                        <small className="text-muted ms-2">
                          ({formatFileSize(task.evidence_file_size)})
                        </small>
                      ) : null}
                      <Button
                        type="link"
                        danger
                        size="small"
                        onClick={() => handleEvidenceRemove(type, index)}
                      >
                        Hapus
                      </Button>
                    </div>
                  ) : (
                    <span className="text-muted small">
                      Belum ada evidence.
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Fragment>
      <Navbar
        title="Create"
        back={true}
        subtitle="Performance Appraisal"
        navigate="/pa/history"
      />
      <div className="container white-back-cr" style={{ minHeight: 500 }}>
        <ImportantInfo />
        <div className="container mt-3 p-3">
          <div className="pa-card">
            <p className="pa-section-title mb-1">Informasi Karyawan</p>
            <small className="text-muted">
              Data otomatis dari Talenta, hubungi HR jika ada ketidaksesuaian.
            </small>
            <div className="row g-3 mt-2">
              <div className="col-md-6">
                <label className="pa-field-label">Nama Lengkap</label>
                <Input value={session.fullname || "-"} disabled />
              </div>
              <div className="col-md-6">
                <label className="pa-field-label">Employee ID</label>
                <Input value={session.employee_id || "-"} disabled />
              </div>
              <div className="col-md-6">
                <label className="pa-field-label">Jabatan</label>
                <Input value={session.job_position || "-"} disabled />
              </div>
              <div className="col-md-6">
                <label className="pa-field-label">Level</label>
                <Input value={session.job_level || "-"} disabled />
              </div>
              <div className="col-md-6">
                <label className="pa-field-label">Departemen</label>
                <Input value={session.organization_name || "-"} disabled />
              </div>
              <div className="col-md-3">
                <label className="pa-field-label">Periode Tahun</label>
                <Input value={currentYear} disabled />
              </div>
            </div>
          </div>

          <Divider orientation="left">Aspek Kinerja</Divider>
          {loadingMaster ? (
            <div className="text-center my-5">
              <Spin size="large" />
            </div>
          ) : performanceMaster.length === 0 ? (
            <p className="text-center text-muted">
              Master performance belum tersedia untuk level ini.
            </p>
          ) : (
            performanceMaster.map((aspect) => (
              <div key={aspect.aspect_code} className="pa-card">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <p className="pa-section-title mb-0">
                      {aspect.aspect_label}
                    </p>
                    <small className="text-muted">{aspect.aspect_code}</small>
                  </div>
                  <span className="pa-aspect-order">
                    Urutan {aspect.order}
                  </span>
                </div>
                <Radio.Group
                  className="pa-performance-options"
                  value={performanceValues[aspect.aspect_code]}
                  onChange={(e) =>
                    handlePerformanceChange(aspect.aspect_code, e.target.value)
                  }
                >
                  {aspect.statements.map((statement) => (
                    <Radio
                      key={`${aspect.aspect_code}-${statement.order}`}
                      value={statement.order}
                      className="pa-performance-option"
                    >
                      <div className="pa-radio-label">
                        <span className="pa-radio-order">
                          {statement.order}.
                        </span>
                        <span>{statement.description}</span>
                      </div>
                    </Radio>
                  ))}
                </Radio.Group>
              </div>
            ))
          )}

          <Divider orientation="left">Kompetensi</Divider>
          {loadingMaster ? (
            <div className="text-center my-5">
              <Spin size="large" />
            </div>
          ) : competencyMaster.length === 0 ? (
            <p className="text-center text-muted">
              Master competency belum tersedia untuk level ini.
            </p>
          ) : (
            competencyMaster.map((comp) => (
              <div key={comp.competency_code} className="pa-card">
                <p className="pa-section-title mb-1">{comp.competency_label}</p>
                {comp.statements.map((statement) => {
                  const key = `${comp.competency_code}_${statement.statement_no}`;
                  return (
                    <div key={key} className="pa-competency-item">
                      <p className="mb-2 fw-medium">
                        {statement.statement_no}. {statement.description}
                      </p>
                      <Radio.Group
                        optionType="default"
                        // buttonStyle=""
                        size="small"
                        value={competencyValues[key]}
                        onChange={(e) => handleCompetencyChange(key, e.target.value)}
                        className="pa-competency-options mt-3"
                      >
                        <Radio.Button value={1}>Tidak Setuju</Radio.Button>
                        <Radio.Button value={2}>Cukup</Radio.Button>
                        <Radio.Button value={3}>Setuju</Radio.Button>
                      </Radio.Group>
                    </div>
                  );
                })}
              </div>
            ))
          )}

          {/* All job level except Non Staff */}
          {!isNonStaff && (
            <>
              <Divider orientation="left">Tugas</Divider>
              {renderTaskSection("12 Bulan Terakhir", pastTasks, "past")}
              {renderTaskSection("12 Bulan Berikutnya", futureTasks, "future")}
            </>
          )}
        </div>
      </div>

      <Affix offsetBottom={0}>
        <div className="pa-submit-bar text-center">
          <Button
            type="primary"
            icon={<SaveOutlined />}
            size="large"
            loading={submitting}
            onClick={handleSubmit}
            className="pa-submit-btn"
          >
            Kirim Penilaian
          </Button>
        </div>
      </Affix>
      <Footer />
    </Fragment>
  );
}

export default PATransactionCreate;
